/**
 * `/api/referrals` wrappers.
 *
 * Only the code lookup is used by this app; `tree` and `history` are public
 * cross-origin endpoints kept here because `routes.api.referrals` declares them
 * and a wrapper is the only sanctioned way to reach a declared route.
 */
import type {
  GetReferralCodeQuery,
  GetReferralCodeResponse,
  GetReferralHistoryResponse,
  GetReferralTreeResponse,
} from "@swarm/shared/schemas";
import {
  getReferralCodeResponseSchema,
  getReferralHistoryResponseSchema,
  getReferralTreeResponseSchema,
} from "@swarm/shared/schemas";

import { routes } from "@/hooks/useRoutes/useRoutes";

import { apiRequest } from "./client";

/**
 * The official referral code minted for a wallet, or `null` if it has none.
 *
 * The value is persisted and later signed into an on-chain transaction, so the
 * caller still validates it with `isValidReferralCode` before storing — being
 * our own API is not the same as being trusted input.
 */
export const getReferralCodeByWalletAddress = (
  query: GetReferralCodeQuery,
): Promise<GetReferralCodeResponse> =>
  apiRequest(
    routes.api.referrals.getReferralCodeByWalletAddress(query),
    getReferralCodeResponseSchema,
  );

/** Licenses bought through a referral code. `code` is the ENCRYPTED form. */
export const getReferralHistory = (code: string): Promise<GetReferralHistoryResponse> =>
  apiRequest(routes.api.referrals.history({ code }), getReferralHistoryResponseSchema);

/** The referral forest, rooted at `wallet` or complete when `fullTree`. */
export const getReferralTree = (params: {
  wallet?: string;
  fullTree?: boolean;
}): Promise<GetReferralTreeResponse> =>
  apiRequest(routes.api.referrals.tree(params), getReferralTreeResponseSchema);
